import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth'
import { Button } from './Button'

type AdminRoute = '/admin/users' | '/admin/features' | '/admin/audit'

const ADMIN_ROUTES: { path: AdminRoute; label: string }[] = [
  { path: '/admin/users', label: 'Users' },
  { path: '/admin/features', label: 'Features' },
  { path: '/admin/audit', label: 'Audit log' },
]

type TopbarProps = {
  title?: string
  /** When set, shows a back link on the left. */
  backTo?: string
  backLabel?: string
  /** Slot to the right of the title (e.g. breadcrumb/plugin icon). */
  centerSlot?: React.ReactNode
}

export function Topbar({ title, backTo, backLabel = '← Home', centerSlot }: TopbarProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    function handle(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', handle)
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('mousedown', handle)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [menuOpen])

  // Close menu when route changes
  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  async function onSignOut() {
    setMenuOpen(false)
    await logout()
    navigate('/login')
  }

  const displayName = user?.displayName ?? user?.username ?? ''

  return (
    <header className="topbar">
      <div className="topbar-left">
        {backTo ? (
          <Link to={backTo} className="topbar-back">
            {backLabel}
          </Link>
        ) : (
          <Link to="/" className="topbar-title">
            macmini
          </Link>
        )}
      </div>
      <div className="topbar-center">
        {title ? <span className="topbar-title">{title}</span> : centerSlot}
      </div>
      <div className="topbar-right">
        <span className="topbar-user">{displayName}</span>
        {user?.isOwner && (
          <div className="topbar-menu" ref={menuRef}>
            <button
              type="button"
              className="topbar-menu-button"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              Admin
            </button>
            {menuOpen && (
              <div className="topbar-menu-list" role="menu">
                {ADMIN_ROUTES.map((r) => (
                  <Link
                    key={r.path}
                    to={r.path}
                    className={`topbar-menu-item ${location.pathname === r.path ? 'active' : ''}`}
                    role="menuitem"
                  >
                    {r.label}
                  </Link>
                ))}
                <hr className="hairline" />
                <button
                  type="button"
                  className="topbar-menu-item"
                  role="menuitem"
                  onClick={onSignOut}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
        {!user?.isOwner && (
          <Button variant="ghost" size="sm" onClick={onSignOut}>
            Sign out
          </Button>
        )}
      </div>
    </header>
  )
}
